import { formatMetricValue, addGtmPropsToHighlightBannerComponent } from '../metrics/metrics-helpers';
import { Helpers } from '../core/src/helpers';

export const DEFAULT_PAGE_SIZE = 48;
export const PAGE_SIZE_LAZY = 6;
export const PAGE_SIZE_LAZY_MOBILE = 2;
const DEFAULT_FACET_ADDONS = {
    value: {
        addOnFacets: [
            {
                facetKey: 'priceFacet',
                facetSettings: [
                    {
                        description: 'Default default price range',
                        pathRegex: null,
                        enabled: true,
                        expanded: true,
                        displaySequence: 1,
                        value: [
                            { label: 'Under $600', value: '0-600' },
                            { label: '$600 to $1000', value: '600-1000' },
                            { label: '$1000 to $1500', value: '1000-1500' },
                            { label: '$1500 and above', value: '1500-99999' },
                        ],
                    },
                ],
            },
        ],
    },
};

/**
 * Loops through facet override component and finds picks the facet settings given the current vanityUrl and filter selections
 * @param {*} vanityUrl
 * @param {*} component
 * @param {*} filters
 * @returns
 */
export const getFacetAddons = Helpers.memoize(
    (vanityUrl, component, filters = {}) => {
        const { value } = component || DEFAULT_FACET_ADDONS;
        const { addOnFacets } = value || {};
        return (addOnFacets || []).reduce((r, facet) => {
            const { facetKey, facetSettings } = facet;
            if (facetSettings.length === 1) {
                r[facetKey] = facetSettings[0];
                //set key as property to match ETR API response
                r[facetKey].key = facetKey;
            } else {
                //find the first best match in the facet settings
                r[facetKey] = facetSettings.find(facetSetting => {
                    const { enabled, maxSubCats, minSubCats, pathRegex, subCats, subCatsExclude } = facetSetting || {};
                    const subCat = filters['subcat'];
                    const subCatCount = Object.keys(subCat || {}).length;
                    try {
                        return (
                            enabled &&
                            (typeof maxSubCats !== 'number' || subCatCount <= maxSubCats) &&
                            (typeof minSubCats !== 'number' || subCatCount >= minSubCats) &&
                            (!subCats || subCats.split(',').reduce((r, cat) => r && cat in subCat, true)) &&
                            (!subCatsExclude ||
                                subCatsExclude.split(',').reduce((r, cat) => r && !(cat in subCat), true)) &&
                            (!pathRegex || new RegExp(pathRegex).test(vanityUrl))
                        );
                    } catch (e) {
                        return false;
                    }
                });
                //set key as property to match ETR API response
                r[facetKey].key = facetKey;
            }

            //index map it so its easier to use in the component
            r[facetKey].valMap = r[facetKey]?.value?.reduce((a, c) => {
                a[c.label] = { ...c };
                return a;
            }, {});
            return r;
        }, {});
    },
    (vanityUrl, component, filters) => {
        return `${vanityUrl}-${component?.key}-${JSON.stringify(filters)}`;
    },
);

export const buildFilterFromUrl = (url, ignorePage = true) => {
    if (!url) return {};
    const facets = url.split(url.includes('&') ? '&' : ';');
    const filters = {};
    const delimeter = '=';
    facets.forEach(facet => {
        const [key, valueString] = facet.split(
            new RegExp(`(?:${delimeter}|${encodeURIComponent(delimeter)})(.*)`, 'ig'),
        );
        if ((ignorePage && key === 'page') || !key) return;
        const value = valueString?.split(',') || [];
        const values = {};
        value.forEach(val => {
            values[val] = val;
        });
        filters[key] = values;
    });
    return filters;
};

export const buildUrlFromFilters = filters => {
    const urlFacets = [];
    Object.keys(filters)
        .sort((a, b) => a.localeCompare(b))
        .forEach(facet => {
            if (typeof filters[facet] === 'object') {
                const keys = Object.keys(filters[facet]).sort();
                if (keys.length < 1) return;
                urlFacets.push(`${facet}=${keys.map(key => key).join(',')}`);
            } else if (filters[facet]) {
                urlFacets.push(`${facet}=${filters[facet]}`);
            }
        });
    return urlFacets.join('&');
};

export const removeFromFilter = (filters, removeFilters) => {
    if (!removeFilters || Object.keys(removeFilters).length === 0) {
        return filters;
    }
    const updatedFilters = { ...filters };
    Object.keys(removeFilters).forEach(key => {
        if (updatedFilters[key]) {
            if (typeof updatedFilters[key] === 'object') {
                Object.keys(removeFilters[key]).forEach(subKey => {
                    delete updatedFilters[key][subKey];
                });
            }
        }
    });
    return updatedFilters;
};

export const getValidIntopicBlogSubcat = urlFilters => {
    const hasSubcatFilter = typeof urlFilters === 'string' && /(\&|^)subcat\=/gi.test(urlFilters);
    const filters = hasSubcatFilter && buildFilterFromUrl(urlFilters.toLowerCase());

    const validSubCats = [];
    const subcatFilter = filters && filters['subcat'];
    if (subcatFilter && subcatFilter['monitors']) {
        validSubCats.push('monitors');
    }

    if (subcatFilter && subcatFilter['pc-components']) {
        validSubCats.push('components');
    }

    if (subcatFilter && (subcatFilter['gaming'] || subcatFilter['gaming-accessories'])) {
        validSubCats.push('gaming');
    }

    if (validSubCats.length > 0) {
        return validSubCats;
    }
};

/**
 * builds finder-results query params
 */
export const getQueryParams = (slug, updatedFilters, params) => {
    const updatedPath = buildUrlFromFilters({ ...updatedFilters });
    return {
        path: `vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}`,
        ...params,
    };
};

export const getPageParam = path => {
    const pageParam = path?.match(/page=(\d+)/);
    return pageParam ? pageParam[1] : 1;
};

export const getPageUrl = (url, page, hasFilters, withQuery = true) => {
    const pageMatch = url?.path?.match(/page=(\d+)/);

    if (!url?.path && page === 1) {
        return `${url.pathname}${withQuery ? url?.query : ''}`;
    }

    const isMulti = url?.path?.includes('&page=');
    const isSingle = url?.path?.includes('page=');
    if (isMulti) {
        return `${url.pathname}/${url.path.replace('&page=' + pageMatch?.[1], page > 1 ? `${'&page=' + page}` : '')}${
            withQuery ? url?.query : ''
        }`;
    } else if (isSingle) {
        return `${url.pathname}${page > 1 ? '/' : ''}${url.path.replace(
            'page=' + pageMatch?.[1],
            page > 1 ? `${'page=' + page}` : '',
        )}${withQuery ? url?.query : ''}`;
    } else {
        return `${url.pathname}/${url.path || ''}${page > 1 ? `${hasFilters > 0 ? '&' : ''}page=${page}` : ''}${
            withQuery ? url?.query : ''
        }`;
    }
};

// Adds GTM values to the VWA banner components (the banners may have the structure { highlightBanner: []} or [{...}])
export const getVwaBannerGtmWithDifferentStructures = ({ component, analyticsData, componentKey }) => {
    const hasHighlightBanner = component && component.highlightBanner;

    if (!component || (!hasHighlightBanner && (!Array.isArray(component) || !component[0]))) return component;

    try {
        if (hasHighlightBanner) {
            return addGtmPropsToHighlightBannerComponent({
                componentKey,
                component,
                analyticsData,
            });
        }

        let [banner] = component;
        let { highlightBanner } = addGtmPropsToHighlightBannerComponent({
            componentKey,
            component: { highlightBanner: banner },
            analyticsData,
        });

        component[0] = {
            ...(highlightBanner || {}),
            gtmActions: highlightBanner.gtmActions,
        };
    } catch (e) {}

    return component;
};

export const getVwaDynamicBannerComponent = component =>
    component && component.highlightBanner
        ? [component.highlightBanner]
        : Array.isArray(component) && component[0]
          ? [component[0]]
          : [];

export const getSearchKey = (path, orderBy) => `${path}${orderBy ? '-' + orderBy : ''}`;

export const sortFacets = ({ expandedFacets, facets }) => {
    try {
        const expandedOrder = expandedFacets.reduce((acc, expanded) => {
            const index = facets.findIndex(item => item.displayName.toLowerCase() === expanded.toLowerCase());
            const facet = facets[index];
            if (facet) {
                acc.push(facet);
                facets.splice(index, 1);
            }

            return acc;
        }, []);

        return [...expandedOrder, ...facets];
    } catch (e) {
        return [...(expandedFacets || []), ...(facets || [])];
    }
};

export const getInputProps = (expanded, placeholder, ariaLabel, preventFocusProp) => {
    return expanded
        ? {
              placeholder,
              'aria-label': ariaLabel,
          }
        : preventFocusProp;
};

export const resolveABTestData = (getABTestVWAProducts, products, filters) => {
    try {
        return typeof getABTestVWAProducts === 'function' ? getABTestVWAProducts(products, filters) : { products };
    } catch (e) {
        return { products };
    }
};

function isRegexLiteral(str) {
    if (typeof str !== 'string') return false;
    return /^\/.*\/[gimsuy]*$/.test(str);
}

function parseRegexString(str) {
    const match = str.match(/^\/(.*)\/([gimsuy]*)$/);
    if (!match) {
        throw new Error('Invalid regex string format');
    }
    const [, pattern, flags] = match;
    return new RegExp(pattern, flags);
}

/**ee filters are in the active filters
 * @param {*} list
 * @param {*} filters
 * @returns {Object} - included: boolean, matches: object of filters that are included in the active filters
 */
export const facetInList = (list, filters, options) => {
    let matches = {};
    const included = list
        ? list.reduce((acc, i) => {
              if (acc) return acc;
              const inclusiveFilters = buildFilterFromUrl(i);
              const isMatch = Object.keys(inclusiveFilters).reduce((acc, facetKey) => {
                  const f = inclusiveFilters[facetKey];
                  return (
                      Object.values(f).reduce((acc, v) => {
                          //check if v is regex
                          if (isRegexLiteral(v)) {
                              try {
                                  const regex = parseRegexString(v);

                                  if (options?.combineFilters) {
                                      const combined = filters[facetKey]
                                          ? Object.values(filters[facetKey]).join(',')
                                          : '';
                                      if (combined && regex.test(combined)) {
                                          matches = { ...matches, [facetKey]: filters[facetKey] };
                                          return true;
                                      }
                                  } else {
                                      if (
                                          filters[facetKey] &&
                                          Object.values(filters[facetKey]).some(k => regex.test(k))
                                      ) {
                                          matches = { ...matches, [facetKey]: filters[facetKey] };
                                          return true;
                                      }
                                  }
                              } catch (e) {}
                          }
                          if (acc) return acc;
                          if (filters[facetKey] && !!filters[facetKey][v]) {
                              matches = { ...matches, [facetKey]: filters[facetKey] };
                              return true;
                          }
                          return false;
                      }, false) && acc
                  );
              }, true);
              return isMatch;
          }, false)
        : false;
    return { included, matches };
};

/**
 *
 * @param {*} filters - active filters object, ex {segm: {Home: "Home"}}
 * @param {*} include - Array of filters to includes, ex ["segm=Home", "segm=Business"]
 * @param {*} exclude - Array of filters to exclude, ex ["segm=Home", "segm=Business"]
 * @returns
 */
export const hideLogo = (filters, include, exclude) => {
    const { included: isIncluded } = facetInList(include, filters, { combineFilters: true });
    const { included: isExcluded, matches: exlusionMatch } = facetInList(exclude, filters, { combineFilters: true });
    const wildCardExclusion = Array.isArray(exclude) && (exclude?.length === 0 || exclude.includes('*'));

    // hide if it contains an explicit exclusion or if it is not included and the exclusion is not a wildcard
    return { hide: isExcluded || (!isIncluded && (wildCardExclusion || isExcluded)), exlusionMatch };
};

export const filterLogos = (filters, logos) => {
    if (!logos || logos.length < 1) return [];
    const { filteredLogos } = logos.reduce(
        (acc, logo) => {
            const { hide } = hideLogo(filters, logo.include, logo.exclude);
            if (!hide) {
                acc.filteredLogos.push(logo);
            }
            return acc;
        },
        { filteredLogos: [] },
    );

    return filteredLogos;
};
